/**
 * User×Week workload heatmap (#64). Complements WorkloadTable (per-person
 * totals) with the missing time axis: rows = people, columns = weeks, cell
 * intensity = open task count in that week. A trailing "Ohne Termin" column
 * holds tasks with no date at all — never guessed onto "this week" (see
 * lib/workloadHeatmap.ts on the server for the full counting model).
 *
 * Accessibility: color alone never carries the information — every cell has
 * a `title` + `aria-label` naming the person, the week, and the value, and
 * non-empty cells also print the count as text.
 */
import type { WorkloadHeatmapResponse } from "../../lib/api";
import { formatHours, heatLevel } from "./chartHelpers";

interface Props {
  data: WorkloadHeatmapResponse;
}

// Opacity steps for the primary-accent overlay — kept as a separate layer
// (not a background applied with a faded `color`) so the count text on top
// always renders at full, readable contrast regardless of intensity.
const LEVEL_OPACITY: Record<0 | 1 | 2 | 3 | 4, number> = {
  0: 0,
  1: 0.15,
  2: 0.35,
  3: 0.55,
  4: 0.85,
};

// Levels 3-4 sit on a fairly saturated fill → pair with the primary's own
// "on-primary" token (matches the existing bg-primary/text-on-primary
// convention elsewhere) instead of a fixed neutral, so contrast holds in
// both themes. Lower levels stay close to the surface, so on-surface fits.
const LEVEL_FG: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "var(--color-on-surface-variant)",
  1: "var(--color-on-surface)",
  2: "var(--color-on-surface)",
  3: "var(--color-on-primary)",
  4: "var(--color-on-primary)",
};

function Cell({
  count,
  hours,
  max,
  personName,
  columnLabel,
}: {
  count: number;
  hours: number;
  max: number;
  personName: string;
  columnLabel: string;
}): preact.JSX.Element {
  const level = heatLevel(count, max);
  const valueText =
    count === 0
      ? "keine offenen Aufgaben"
      : `${count} ${count === 1 ? "offene Aufgabe" : "offene Aufgaben"}, ${formatHours(hours)} geschätzt`;
  const label = `${personName}, ${columnLabel}: ${valueText}`;

  return (
    <td class="p-0.5 text-center align-middle" title={label} aria-label={label}>
      <div class="relative min-w-[38px] h-7 rounded-sm overflow-hidden">
        <div
          class="absolute inset-0"
          style={{ background: "var(--color-primary)", opacity: LEVEL_OPACITY[level] }}
          aria-hidden="true"
        />
        <div
          class="relative flex items-center justify-center h-full text-label font-medium tabular-nums"
          style={{ color: LEVEL_FG[level] }}
        >
          {count > 0 ? count : ""}
        </div>
      </div>
    </td>
  );
}

export function WorkloadHeatmap({ data }: Props): preact.JSX.Element {
  if (data.users.length === 0) {
    return (
      <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
        Keine offenen Aufgaben mit Zuweisungen
      </p>
    );
  }

  const max = Math.max(
    1,
    ...data.users.flatMap((u) => u.weeks.map((w) => w.count)),
    ...data.users.map((u) => u.no_date.count)
  );

  return (
    <div class="overflow-x-auto">
      <table class="border-collapse text-micro">
        <thead>
          <tr class="text-left text-[color:var(--color-on-surface-variant)]">
            <th class="py-1 pr-3 font-medium">Nutzer</th>
            {data.week_columns.map((col) => (
              <th key={col.key} class="py-1 font-medium text-center whitespace-nowrap">
                {col.label}
              </th>
            ))}
            <th class="py-1 pl-2 font-medium text-center whitespace-nowrap">Ohne Termin</th>
          </tr>
        </thead>
        <tbody>
          {data.users.map((u) => (
            <tr
              key={u.user_id}
              class="border-t border-[color:var(--color-outline-variant)]"
            >
              <td class="py-1 pr-3 font-medium text-[color:var(--color-on-surface)] max-w-[120px] truncate">
                {u.user_name}
              </td>
              {u.weeks.map((cell, i) => (
                <Cell
                  key={data.week_columns[i]!.key}
                  count={cell.count}
                  hours={cell.estimated_hours}
                  max={max}
                  personName={u.user_name}
                  columnLabel={`Woche ${data.week_columns[i]!.label}`}
                />
              ))}
              <Cell
                count={u.no_date.count}
                hours={u.no_date.estimated_hours}
                max={max}
                personName={u.user_name}
                columnLabel="ohne Termin"
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
